import { CountryInfo, countriesDictionary } from 'sonata-static-storage/lib/samples/countries.samples';

import type { BidModelInterface } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { MediaTypeType } from 'sonata-static-storage/lib/schemas/mediaType/MediaType.types';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

export function getUserId(user: UserInterface): string {
  const userId = user._id || user.id;
  if (!userId) {
    throw new Error(`getUserId - id not found for user <${user}>`);
  }

  return userId;
}

export function getUserMediaTypes(user: UserInterface) {
  const mediaTypes = user?.mediaTypes;
  if (!mediaTypes || !mediaTypes[0]) {
    const countryISOCode = user?.country;
    const countryData = countriesDictionary[countryISOCode];
    if (countryData) {
      return countryData.mediaTypes;
    }
    return countriesDictionary.default.mediaTypes;
  }

  return mediaTypes;
}

export function getUserPricingModels(user: UserInterface) {
  let pricingModels = structuredClone(user.pricingModels);
  const mediaTypes = getUserMediaTypes(user).filter(mediaType => mediaType !== 'hybrid') as Exclude<
    MediaTypeType,
    'hybrid'
  >[];

  let nonePricingModels = true;
  mediaTypes.forEach(mediaType => {
    if (pricingModels[mediaType] && pricingModels[mediaType][0]) {
      nonePricingModels = false;
    }
  });

  if (nonePricingModels) {
    let countryPricingModels: CountryInfo['pricingModels'] = [];
    const countryISOCode = user.country;
    const countryData = countriesDictionary[countryISOCode];
    if (countryData) {
      countryPricingModels = countryData.pricingModels;
    } else {
      countryPricingModels = countriesDictionary.default.pricingModels;
    }
    pricingModels = {} as UserInterface['pricingModels'];
    mediaTypes.forEach(mediaType => {
      pricingModels[mediaType] = countryPricingModels;
    });

    return pricingModels;
  }

  const sortedPricingModels = {} as Record<string, any>;
  Object.entries(pricingModels).forEach(([mediaType, pModels]) => {
    const unsortedModels = pModels
      .map(pModel => {
        const orderedPModel = taptap.sortedPricingModels.find(
          (sortedModel: Record<string, any>) => sortedModel.model === pModel
        );
        return orderedPModel
          ? {
              index: orderedPModel.index,
              pModel
            }
          : null;
      })
      .filter(item => item !== null);

    const sorted = unsortedModels.sort((a, b) => a!.index - b!.index).map(item => item!.pModel);

    sortedPricingModels[mediaType] = [...new Set(sorted)];
  });

  return sortedPricingModels;
}

export function getUserBidMargin(mediaType: Exclude<MediaTypeType, 'hybrid'>, user: UserInterface) {
  let margin;
  if (user.bidMargin && typeof user.bidMargin[mediaType] !== 'undefined') {
    margin = user.bidMargin[mediaType];
  } else if (countriesDictionary[user.country]) {
    margin = countriesDictionary[user.country].bidMargin;
  } else {
    margin = countriesDictionary.default.bidMargin;
  }

  return margin;
}

export function getUserMinimumBidPrice(
  mediaType: Exclude<MediaTypeType, 'hybrid'>,
  pricingModel: BidModelInterface['bid']['pricingModel'],
  user: UserInterface
) {
  const userMinimumBidPrices = user.minimumBidPrices;
  if (userMinimumBidPrices && userMinimumBidPrices[mediaType] && userMinimumBidPrices[mediaType][pricingModel]) {
    return userMinimumBidPrices[mediaType][pricingModel];
  }
  return taptap.pricingModels[mediaType][pricingModel].defaultBid.amount;
}

/** Default Open/Premium split for a hybrid marketplace: the user's own, else the global one. */
export function getUserMarketplaceDistribution(user: UserInterface): { openRtb: number; pmp: number } {
  return {
    openRtb: user?.marketplaceDistribution?.openRtb ?? taptap.marketplaceDistribution.openRtb,
    pmp: user?.marketplaceDistribution?.pmp ?? taptap.marketplaceDistribution.pmp
  };
}

export function getUserBidModelsAndMargins(
  previousMediaType: MediaTypeType,
  newMediaType: MediaTypeType,
  bidModels: BidModelInterface[],
  user: UserInterface
) {
  const targetMediaType = newMediaType;
  const userPricingModels = user.pricingModels || {};
  const currentBidModels = structuredClone(bidModels);

  const prevTypeNormalized = previousMediaType === 'hybrid' ? 'openRtb' : previousMediaType;
  const prevBidModel = currentBidModels.find(bm => bm.mediaType === prevTypeNormalized);
  const previousPricingModel = prevBidModel?.bid?.pricingModel;

  const newBidModels = [];
  const margins = {} as PricingModelConfig['margin'];

  if (targetMediaType === 'hybrid') {
    const pmpModels = userPricingModels.pmp || [];
    const ortbModels = userPricingModels.openRtb || [];
    const commonPricingModels = pmpModels.filter(m => ortbModels.includes(m));
    const pickedPricingModel =
      previousPricingModel && commonPricingModels.includes(previousPricingModel)
        ? previousPricingModel
        : commonPricingModels[0];

    const currentBidModelPrice = currentBidModels.find(bm => bm.mediaType === previousMediaType)?.bid?.price ?? 0;

    (['openRtb', 'pmp'] as const).forEach(mediaType => {
      const minPrice = getUserMinimumBidPrice(mediaType, pickedPricingModel, user);
      const price = Math.max(currentBidModelPrice, minPrice);

      newBidModels.push({
        bid: {
          dynamicBidPrice: false,
          dynamicBidPriceManualSet: false,
          price,
          pricingModel: pickedPricingModel
        },
        distribution: getUserMarketplaceDistribution(user)[mediaType],
        mediaType
      });

      const userBidMargin = getUserBidMargin(mediaType, user);
      margins[mediaType] =
        typeof userBidMargin === 'number'
          ? userBidMargin
          : mediaType === 'openRtb'
            ? Number(userBidMargin[mediaType]) || 0
            : 0;
    });
  } else {
    const pricingModels = userPricingModels[targetMediaType] || [];
    const pickedPricingModel =
      previousPricingModel && pricingModels.includes(previousPricingModel) ? previousPricingModel : pricingModels[0];

    const price =
      previousMediaType === 'hybrid'
        ? currentBidModels.find(bm => bm.mediaType === targetMediaType)?.bid?.price
        : getUserMinimumBidPrice(targetMediaType, pickedPricingModel, user);

    newBidModels.push({
      bid: {
        dynamicBidPrice: false,
        dynamicBidPriceManualSet: false,
        price,
        pricingModel: pickedPricingModel
      },
      mediaType: targetMediaType
    });

    const userBidMargin = getUserBidMargin(targetMediaType, user);
    margins[targetMediaType] =
      typeof userBidMargin === 'number'
        ? userBidMargin
        : targetMediaType === 'openRtb'
          ? Number(userBidMargin[targetMediaType]) || 0
          : 0;
  }

  return {
    bidModels: newBidModels,
    margins
  };
}
